// src/screens/catalogUpload/hooks/useCatUpload.ts
import { useCallback, useState, useEffect, useRef } from 'react'
import { CatUploadInterface } from '../../../common/types/interfaces'
import { getSalesApi } from '../../../salesApi/salesApi'
import { CatUploadStatus } from '../../../common/types/enums'

export const initialData: CatUploadInterface = {
  fileName: '',
  status: CatUploadStatus.PENDING,
  clusterId: 0,
  categoriesImported: 0,
  itemsImported: 0,
  imagesImported: 0,
  importedAt: new Date().toISOString(),
}

export const useCatUpload = () => {
  const [modalActive, setModalActive] = useState<boolean>(false)
  const [edit, setEdit] = useState<boolean>(false)
  const [salesApi] = useState(getSalesApi())
  const [data, setData] = useState<CatUploadInterface | undefined>()
  const [detailsModalActive, setDetailsModalActive] = useState<boolean>(false)
  const [current, setCurrent] = useState<CatUploadInterface>()
  const [list, setList] = useState<CatUploadInterface[]>([])
  const [loading, setLoading] = useState(true)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const getList = useCallback(async () => {
    try {
      setLoading(true)
      const result = await salesApi.getCatUploadHistory({ sort: 'importedAt', order: 'DESC' })
      if (result) setList(result)
    } catch (error) {
      console.error('Failed to load catalog upload history', error)
    } finally {
      setLoading(false)
    }
  }, [salesApi])

  // Пуллинг: обновляем список, если есть записи в статусе processing
  const startPolling = useCallback(() => {
    if (intervalRef.current) return

    intervalRef.current = setInterval(() => {
      const hasProcessing = list.some((item) => item.status === CatUploadStatus.PROCESSING)
      if (hasProcessing) {
        getList()
      }
    }, 3000)
  }, [list, getList])

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const startEdit = (el: CatUploadInterface) => {
    if (detailsModalActive) setDetailsModalActive(false)
    setCurrent({ ...el })
    setEdit(true)
    setModalActive(true)
  }

  const startCreate = () => {
    setCurrent(undefined)
    setEdit(false)
    setModalActive(true)
  }

  const showDetails = useCallback((el: CatUploadInterface) => {
    setCurrent(el)
    setDetailsModalActive(true)
  }, [])

  // Загружаем список при монтировании
  useEffect(() => {
    getList()
  }, [getList])

  // Запускаем пуллинг, если есть processing записи
  useEffect(() => {
    const hasProcessing = list.some((item) => item.status === CatUploadStatus.PROCESSING)
    if (hasProcessing) {
      startPolling()
    } else {
      stopPolling()
    }

    return () => stopPolling()
  }, [list, startPolling, stopPolling])

  useEffect(() => {
    if (!modalActive) setData(undefined)
  }, [modalActive])

  return {
    getList,
    modalActive,
    setModalActive,
    startEdit,
    edit,
    data,
    setData,
    list,
    detailsModalActive,
    setDetailsModalActive,
    showDetails,
    current,
    initialData,
    startCreate,
    loading,
  }
}
